import { useNavigate } from 'react-router-dom';
import Avatar from '@/components/base/Avatar';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import StatusBadge from '@/components/base/StatusBadge';
import { formatDate, formatMoney } from '@/utils/format';
import type { Payout } from '@/types/portal';

interface PayoutTableProps {
  rows: Payout[];
  canManage: boolean;
  onRetry: (payout: Payout) => void;
}

export default function PayoutTable({ rows, canManage, onRetry }: PayoutTableProps) {
  const navigate = useNavigate();

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-xl text-foreground-500">
          <i className="ri-bank-card-line" />
        </span>
        <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">
          No payouts match
        </h3>
        <p className="mt-1 max-w-sm text-sm text-foreground-600">
          Try another status tab or clear the market and search filters.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-background-200 bg-background-50">
      <table className="row-accent w-full min-w-[1040px] border-collapse text-left">
        <thead>
          <tr className="border-b border-background-200 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
            <th className="px-5 py-3 font-semibold">Creator</th>
            <th className="px-3 py-3 font-semibold">Campaign</th>
            <th className="px-3 py-3 font-semibold">Rail</th>
            <th className="px-3 py-3 font-semibold">Amount</th>
            <th className="px-3 py-3 font-semibold">Status</th>
            <th className="px-3 py-3 font-semibold">Exception</th>
            <th className="px-5 py-3 text-right font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => navigate(`/creators/${row.creatorId}`)}
              className="cursor-pointer border-b border-background-100 last:border-0 transition-colors hover:bg-background-100"
            >
              <td className="px-5 py-3.5">
                <div className="flex items-center gap-2.5">
                  <Avatar src={row.avatar} alt={row.creatorName} size="sm" />
                  <div className="min-w-0">
                    <p className="text-sm text-foreground-900">{row.creatorName}</p>
                    <p className="text-xs text-foreground-500">
                      {row.reference} · {row.market}
                    </p>
                  </div>
                </div>
              </td>
              <td className="px-3 py-3.5">
                <p className="max-w-[190px] truncate text-sm text-foreground-700">{row.campaign}</p>
                <p className="text-xs text-foreground-400">
                  {row.status === 'Completed' && row.completedAt
                    ? `Settled ${formatDate(row.completedAt)}`
                    : `Scheduled ${formatDate(row.scheduledAt)}`}
                </p>
              </td>
              <td className="px-3 py-3.5">
                <span className="text-xs text-foreground-600">{row.rail}</span>
                {row.batchRef ? (
                  <p className="text-[11px] text-foreground-400">{row.batchRef}</p>
                ) : null}
              </td>
              <td className="px-3 py-3.5">
                <p className="text-sm font-medium text-foreground-950">
                  {formatMoney(row.amount, row.currency)}
                </p>
                {row.attempts > 1 ? (
                  <p className="text-[11px] text-primary-700">{row.attempts} attempts</p>
                ) : null}
              </td>
              <td className="px-3 py-3.5">
                <StatusBadge status={row.status} />
              </td>
              <td className="px-3 py-3.5">
                {row.exception ? (
                  <span className="block max-w-[220px] text-xs text-primary-700">{row.exception}</span>
                ) : (
                  <span className="text-xs text-foreground-400">—</span>
                )}
              </td>
              <td className="px-5 py-3.5 text-right">
                {row.status === 'Failed' ? (
                  <Button
                    size="sm"
                    variant="outline"
                    icon="ri-refresh-line"
                    disabled={!canManage}
                    onClick={(event) => {
                      event.stopPropagation();
                      onRetry(row);
                    }}
                  >
                    Retry
                  </Button>
                ) : row.status === 'On hold' ? (
                  <Badge tone="primary" icon="ri-lock-line">
                    Held
                  </Badge>
                ) : (
                  <span className="text-xs text-foreground-400">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}